import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  Container,
  Tabs,
  Tab,
  Button,
  Badge,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip
} from '@mui/material';
import { 
  ShoppingCart as CartIcon,
  Add as AddIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Tune as TuneIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { copyStorageItemsToExcel } from '../utils/storageExcelExport';
import BusinessItems from './storage/BusinessItems';
import OfficeItems from './storage/OfficeItems';
import ExhibitionItems from './storage/ExhibitionItems';
import DiscoverySpaceItems from './storage/DiscoverySpaceItems';
import SouvenirItems from './storage/SouvenirItems';
import StickersItems from './storage/StickersItems';
import WorkToolsItems from './storage/WorkToolsItems';
import WorkshopItems from './storage/WorkshopItems';
import KitchenItems from './storage/KitchenItems';
import ShoppingCart from './storage/ShoppingCart';
import type { CartTransferMode } from './storage/cartTypes';
import TransactionLog from './storage/TransactionLog';
import AddItemDialog from './storage/AddItemDialog';

interface StorageItem {
  id: string;
  name: string;
  description: string;
  category: 'business' | 'office' | 'exhibition' | 'souvenirs' | 'work_tools' | 'discovery_space' | 'stickers' | 'workshop' | 'kitchen';
  quantity: number;
  storeQuantity?: number;
  takenQuantity?: number;
  minQuantity: number;
  unit: string;
  location: string;

  supplier: string;
  savikaina?: number;
  pardavimoKaina?: number;
  creator: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface CartItem {
  itemId: string;
  name: string;
  quantity: number;
  maxQuantity: number;
  unit: string;
  transferSource: 'warehouse' | 'store' | 'taken';
}

const CATEGORIES = [
  { key: 'business' as const, labelKey: 'storage.categories.business' },
  { key: 'office' as const, labelKey: 'storage.categories.office' },
  { key: 'exhibition' as const, labelKey: 'storage.categories.exhibition' },
  { key: 'discovery_space' as const, labelKey: 'storage.categories.discovery_space' },
  { key: 'souvenirs' as const, labelKey: 'storage.categories.souvenirs' },
  { key: 'stickers' as const, labelKey: 'storage.categories.stickers' },
  { key: 'work_tools' as const, labelKey: 'storage.categories.work_tools' },
  { key: 'workshop' as const, labelKey: 'storage.categories.workshop' },
  { key: 'kitchen' as const, labelKey: 'storage.categories.kitchen' },
];

const STORAGE_VISIBLE_KEY = 'storageVisibleCategories';
const STORAGE_ACTIVE_KEY = 'storageActiveCategory';

const loadVisibleCategories = (): string[] => {
  try {
    const saved = localStorage.getItem(STORAGE_VISIBLE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.every((k) => typeof k === 'string')) {
        return parsed.filter((k) => CATEGORIES.some((c) => c.key === k));
      }
    }
  } catch {}
  return [];
};

const loadActiveCategory = (visible: string[]): string => {
  if (visible.length === 0) return '';
  try {
    const saved = localStorage.getItem(STORAGE_ACTIVE_KEY);
    if (saved && visible.includes(saved)) return saved;
  } catch {}
  return visible[0];
};

const Storage: React.FC = () => {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuth();
  const [visibleCategories, setVisibleCategories] = useState<string[]>(loadVisibleCategories);
  const [activeCategory, setActiveCategory] = useState<string>(() => loadActiveCategory(loadVisibleCategories()));
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [pickerSelected, setPickerSelected] = useState<Record<string, boolean>>({});
  const [cartOpen, setCartOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartTransferMode, setCartTransferMode] = useState<CartTransferMode>('warehouse');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    const saved = localStorage.getItem('storageViewMode');
    return (saved === 'list' || saved === 'cards') ? saved : 'cards';
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    setCartTransferMode('warehouse');
    setCartItems([]);
  }, [activeCategory]);

  useEffect(() => {
    if (visibleCategories.length > 0 && !visibleCategories.includes(activeCategory)) {
      const newActive = visibleCategories[0];
      setActiveCategory(newActive);
      localStorage.setItem(STORAGE_ACTIVE_KEY, newActive);
    }
  }, [visibleCategories, activeCategory]);

  const activeTabIndex = visibleCategories.indexOf(activeCategory);
  const tabValue = activeTabIndex >= 0 ? activeTabIndex : 0;

  useEffect(() => {
    if (visibleCategories.length === 0) {
      const initial: Record<string, boolean> = {};
      CATEGORIES.forEach((c) => { initial[c.key] = false; });
      setPickerSelected((prev) => (Object.keys(prev).length === 0 ? initial : prev));
    }
  }, [visibleCategories.length]);

  const saveVisibleCategories = (categories: string[]) => {
    setVisibleCategories(categories);
    localStorage.setItem(STORAGE_VISIBLE_KEY, JSON.stringify(categories));
    if (categories.length > 0 && !categories.includes(activeCategory)) {
      const newActive = categories[0];
      setActiveCategory(newActive);
      localStorage.setItem(STORAGE_ACTIVE_KEY, newActive);
    }
  };

  const handleActiveCategoryChange = (key: string) => {
    setActiveCategory(key);
    localStorage.setItem(STORAGE_ACTIVE_KEY, key);
  };

  const handleViewModeChange = (_e: React.MouseEvent<HTMLElement>, newMode: 'cards' | 'list' | null) => {
    if (newMode) {
      setViewMode(newMode);
      localStorage.setItem('storageViewMode', newMode);
    }
  };

  const openManageDialog = () => {
    const selected: Record<string, boolean> = {};
    CATEGORIES.forEach((c) => {
      selected[c.key] = visibleCategories.includes(c.key);
    });
    setPickerSelected(selected);
    setManageDialogOpen(true);
  };

  const handleManageSave = () => {
    const selected = Object.entries(pickerSelected)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (selected.length === 0) return;
    saveVisibleCategories(selected);
    setManageDialogOpen(false);
  };

  const handlePickerToggle = (key: string, checked: boolean) => {
    setPickerSelected((prev) => ({ ...prev, [key]: checked }));
  };

  const addToCart = (item: StorageItem, quantity: number, transferSource: CartTransferMode = 'warehouse') => {
    const maxQuantity = transferSource === 'store'
      ? (item.storeQuantity ?? 0)
      : transferSource === 'taken'
        ? (item.takenQuantity ?? 0)
        : item.quantity;
    if (maxQuantity <= 0) return;

    const existingItem = cartItems.find(
      cartItem => cartItem.itemId === item.id && cartItem.transferSource === transferSource
    );
    
    if (existingItem) {
      const newQuantity = existingItem.quantity + quantity;
      if (newQuantity <= maxQuantity) {
        setCartItems(prev => prev.map(cartItem => 
          cartItem.itemId === item.id && cartItem.transferSource === transferSource
            ? { ...cartItem, quantity: newQuantity, maxQuantity }
            : cartItem
        ));
      }
    } else {
      setCartItems(prev => [...prev, {
        itemId: item.id,
        name: item.name,
        quantity,
        maxQuantity,
        unit: item.unit,
        transferSource
      }]);
    }
  };

  const handleCartTransferModeChange = (mode: CartTransferMode) => {
    setCartTransferMode(mode);
    setCartItems([]);
  };

  const removeFromCart = (itemId: string, transferSource?: CartTransferMode) => {
    setCartItems(prev => prev.filter(item =>
      transferSource
        ? !(item.itemId === itemId && item.transferSource === transferSource)
        : item.itemId !== itemId
    ));
  };

  const updateCartQuantity = (itemId: string, quantity: number) => {
    setCartItems(prev => prev.map(item => 
      item.itemId === itemId ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const handleCheckout = () => {
    // This will be handled by the ShoppingCart component
    setCartOpen(false);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleAddItem = () => {
    setAddItemOpen(true);
  };

  const handleItemAdded = () => {
    setAddItemOpen(false);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCopyToExcel = async () => {
    if (!activeCategory) return;

    setExportLoading(true);
    setExportError(null);

    try {
      const params = new URLSearchParams({ sortBy: 'name', sortOrder: 'ASC' });
      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_ITEMS_BY_CATEGORY(activeCategory)}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success || !Array.isArray(data.items)) {
        throw new Error(data.message || 'Failed to fetch items');
      }

      await copyStorageItemsToExcel(data.items, {
        includeStoreQuantity: activeCategory === 'souvenirs',
        includeTakenQuantity: activeCategory === 'workshop' || activeCategory === 'kitchen'
      });

      alert(t('storage.copyToExcelSuccess'));
    } catch (err) {
      console.error('Error copying storage to Excel:', err);
      setExportError(t('storage.copyToExcelError'));
    } finally {
      setExportLoading(false);
    }
  };

  const renderCategoryContent = (categoryKey: string) => {
    const props = {
      refreshTrigger,
      onAddToCart: addToCart,
      viewMode,
      ...(categoryKey === 'souvenirs' || categoryKey === 'workshop' || categoryKey === 'kitchen'
        ? { cartTransferMode, onCartTransferModeChange: handleCartTransferModeChange }
        : {})
    };
    switch (categoryKey) {
      case 'business': return <BusinessItems key="business" {...props} />;
      case 'office': return <OfficeItems key="office" {...props} />;
      case 'exhibition': return <ExhibitionItems key="exhibition" {...props} />;
      case 'discovery_space': return <DiscoverySpaceItems key="discovery_space" {...props} />;
      case 'souvenirs': return <SouvenirItems key="souvenirs" {...props} />;
      case 'stickers': return <StickersItems key="stickers" {...props} />;
      case 'work_tools': return <WorkToolsItems key="work_tools" {...props} />;
      case 'workshop': return <WorkshopItems key="workshop" {...props} />;
      case 'kitchen': return <KitchenItems key="kitchen" {...props} />;
      default: return null;
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ p: 3 }}>
        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
            {exportError}
          </Alert>
        )}

        {/* Header with Cart */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
            🗄️ {t('navigation.storage', 'Sandėliukas')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{ borderColor: '#7b1fa2' }}
            >
              <ToggleButton value="cards" aria-label="Kortelės">
                <Tooltip title="Kortelės">
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <ViewModuleIcon />
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="list" aria-label="Sąrašas">
                <Tooltip title="Sąrašas">
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <ViewListIcon />
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            {activeCategory && hasPermission('storage.view') && (
              <Button
                variant="outlined"
                startIcon={exportLoading ? <CircularProgress size={18} /> : <ExcelIcon />}
                onClick={handleCopyToExcel}
                disabled={exportLoading}
                sx={{
                  borderColor: '#7b1fa2',
                  color: '#7b1fa2',
                  '&:hover': { borderColor: '#6a1b9a', backgroundColor: '#f3e5f5' }
                }}
              >
                {t('storage.copyToExcel')}
              </Button>
            )}
            {hasPermission('storage.manage') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                sx={{ backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}
              >
                Pridėti prekę
              </Button>
            )}
            
            <Button
              variant="outlined"
              startIcon={<CartIcon />}
              onClick={() => setCartOpen(true)}
              sx={{ 
                borderColor: '#7b1fa2', 
                color: '#7b1fa2',
                '&:hover': { borderColor: '#6a1b9a', backgroundColor: '#f3e5f5' }
              }}
            >
              {t('storage.cart', 'Krepšelis')}
              {activeCategory === 'souvenirs' && hasPermission('storage.edit') && cartTransferMode === 'store' && (
                <Chip
                  label={t('storage.souvenirs.cartFromStore')}
                  size="small"
                  sx={{ ml: 1, height: 22, fontSize: '0.7rem' }}
                />
              )}
              {(activeCategory === 'workshop' || activeCategory === 'kitchen') && hasPermission('storage.edit') && cartTransferMode === 'taken' && (
                <Chip
                  label={t(activeCategory === 'kitchen' ? 'storage.kitchen.cartFromTaken' : 'storage.workshop.cartFromTaken')}
                  size="small"
                  sx={{ ml: 1, height: 22, fontSize: '0.7rem' }}
                />
              )}
              {cartItems.length > 0 && (
                <Badge 
                  badgeContent={cartItems.length} 
                  color="error" 
                  sx={{ ml: 1 }}
                />
              )}
            </Button>
          </Box>
        </Box>

        {/* Category picker (first visit / no selection) */}
        {visibleCategories.length === 0 ? (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#7b1fa2' }}>
              {t('storage.selectCategories')}
            </Typography>
            <FormGroup>
              {CATEGORIES.map((cat) => (
                <FormControlLabel
                  key={cat.key}
                  control={
                    <Checkbox
                      checked={pickerSelected[cat.key] ?? false}
                      onChange={(e) => handlePickerToggle(cat.key, e.target.checked)}
                      sx={{ color: '#7b1fa2', '&.Mui-checked': { color: '#7b1fa2' } }}
                    />
                  }
                  label={t(cat.labelKey)}
                />
              ))}
            </FormGroup>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => {
                  const selected = Object.entries(pickerSelected)
                    .filter(([, v]) => v)
                    .map(([k]) => k);
                  if (selected.length > 0) {
                    saveVisibleCategories(selected);
                    setActiveCategory(selected[0]);
                    localStorage.setItem(STORAGE_ACTIVE_KEY, selected[0]);
                  }
                }}
                disabled={Object.values(pickerSelected).filter(Boolean).length === 0}
                sx={{ backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}
              >
                {t('storage.save')}
              </Button>
              {Object.values(pickerSelected).filter(Boolean).length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {t('storage.selectAtLeastOne')}
                </Typography>
              )}
            </Box>
          </Card>
        ) : (
          <>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tabs
                value={tabValue}
                onChange={(_, idx) => handleActiveCategoryChange(visibleCategories[idx])}
                sx={{
                  flex: 1,
                  '& .MuiTab-root': {
                    color: 'text.secondary',
                    '&.Mui-selected': { color: '#7b1fa2', fontWeight: 'bold' }
                  }
                }}
              >
                {visibleCategories.map((key) => {
                  const cat = CATEGORIES.find((c) => c.key === key);
                  return <Tab key={key} label={cat ? t(cat.labelKey) : key} />;
                })}
              </Tabs>
              <Tooltip title={t('storage.manageCategories')}>
                <IconButton onClick={openManageDialog} size="small" sx={{ color: '#7b1fa2' }}>
                  <TuneIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Tab Content */}
            {renderCategoryContent(activeCategory)}
          </>
        )}

        {/* Transaction Log */}
        <Box sx={{ mt: 4 }}>
          <TransactionLog activeCategory={activeCategory} />
        </Box>

        {/* Shopping Cart Dialog */}
        <ShoppingCart
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          items={cartItems}
          onRemoveItem={removeFromCart}
          onUpdateQuantity={updateCartQuantity}
          onClearCart={clearCart}
          onCheckout={handleCheckout}
          activeCategory={activeCategory}
          canTransfer={hasPermission('storage.edit')}
          cartTransferMode={cartTransferMode}
          onCartTransferModeChange={handleCartTransferModeChange}
        />

        {/* Add Item Dialog */}
        <AddItemDialog
          open={addItemOpen}
          onClose={() => setAddItemOpen(false)}
          onItemAdded={handleItemAdded}
          defaultCategory={(activeCategory || 'business') as 'business' | 'office' | 'exhibition' | 'souvenirs' | 'work_tools' | 'discovery_space' | 'stickers' | 'workshop' | 'kitchen'}
        />

        {/* Manage Categories Dialog */}
        <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('storage.manageCategories')}</DialogTitle>
          <DialogContent>
            <FormGroup sx={{ pt: 1 }}>
              {CATEGORIES.map((cat) => (
                <FormControlLabel
                  key={cat.key}
                  control={
                    <Checkbox
                      checked={pickerSelected[cat.key] ?? false}
                      onChange={(e) => handlePickerToggle(cat.key, e.target.checked)}
                      sx={{ color: '#7b1fa2', '&.Mui-checked': { color: '#7b1fa2' } }}
                    />
                  }
                  label={t(cat.labelKey)}
                />
              ))}
            </FormGroup>
            {Object.values(pickerSelected).filter(Boolean).length === 0 && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {t('storage.selectAtLeastOne')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManageDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleManageSave}
              disabled={Object.values(pickerSelected).filter(Boolean).length === 0}
              sx={{ backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}
            >
              {t('storage.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Storage;
